import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { SITE_NAME, OG_IMAGE, getPageSeo, buildJsonLd } from "@/seo";

function upsertMeta(key: string, content: string, attribute: "name" | "property" = "name") {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    existing.setAttribute("content", content);
    return;
  }
  const tag = document.createElement("meta");
  tag.setAttribute(attribute, key);
  tag.setAttribute("content", content);
  document.head.appendChild(tag);
}

function upsertCanonical(href: string) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    existing.setAttribute("href", href);
    return;
  }
  const link = document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", href);
  document.head.appendChild(link);
}

/**
 * Keeps the document head in sync with the route the reader is on.
 * A single-page app shares one index.html, so without this every one of the
 * seven pages would present the exact same title, description and canonical
 * URL to search engines and social cards.
 */
export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getPageSeo(pathname);

    document.title = seo.title;

    upsertMeta("description", seo.description);
    upsertMeta("robots", seo.robots);
    upsertMeta("og:title", seo.title, "property");
    upsertMeta("og:description", seo.description, "property");
    upsertMeta("og:url", seo.canonical, "property");
    upsertMeta("og:site_name", SITE_NAME, "property");
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:image", OG_IMAGE, "property");
    upsertMeta("og:image:width", "1200", "property");
    upsertMeta("og:image:height", "630", "property");
    upsertMeta("og:image:alt", `${SITE_NAME} — a visual guide to forex order types`, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", seo.title);
    upsertMeta("twitter:description", seo.description);
    upsertMeta("twitter:image", OG_IMAGE);

    upsertCanonical(seo.canonical);

    const schemaId = "fx-order-types-site-schema";
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) existingSchema.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = schemaId;
    script.text = JSON.stringify(buildJsonLd(seo));
    document.head.appendChild(script);
  }, [pathname]);

  return null;
}